import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IBadge } from '@clickersphere/core/entities/IBadge';
import BadgeService from '@clickersphere/core/services/BadgeService';
import { LEADS_QUERY_KEY, LeadQueryData } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { BADGES_QUERY_KEY, BadgeQueryData } from './useBadges';

interface IAttachBadgeVars {
  leadId: string;
  badgeId: string;
}

export function useAttachBadge() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: attachBadgeIsPending, mutateAsync: attachBadgeAsync } =
    useMutation({
      mutationFn: async ({ leadId, badgeId }: IAttachBadgeVars) =>
        BadgeService.attachToLead(leadId, badgeId),
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        });

        const oldBadges = queryClient.getQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
        );
        const oldLeads = queryClient.getQueryData<LeadQueryData[]>(
          buildQueryKey(LEADS_QUERY_KEY),
        );
        const oldLead = queryClient.getQueryData<LeadQueryData>(
          buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        );

        const targetBadge = oldBadges?.find((b) => b.id === variables.badgeId);
        const cachedLead =
          (oldLead?.id === variables.leadId ? oldLead : undefined) ??
          oldLeads?.find((l) => l.id === variables.leadId);
        const alreadyAttached =
          cachedLead?.badges?.some((b) => b.id === variables.badgeId) ?? false;

        if (!alreadyAttached) {
          queryClient.setQueryData<BadgeQueryData[]>(
            buildQueryKey(BADGES_QUERY_KEY),
            (old) =>
              old?.map((badge) =>
                badge.id === variables.badgeId
                  ? { ...badge, usageCount: badge.usageCount + 1 }
                  : badge,
              ),
          );

          if (targetBadge) {
            const badgeForLead: IBadge = {
              ...targetBadge,
              usageCount: targetBadge.usageCount + 1,
            };

            queryClient.setQueryData<LeadQueryData[]>(
              buildQueryKey(LEADS_QUERY_KEY),
              (old) =>
                old?.map((lead) => {
                  if (lead.id !== variables.leadId) return lead;
                  const existing = lead.badges ?? [];
                  if (existing.some((b) => b.id === variables.badgeId)) {
                    return lead;
                  }
                  return { ...lead, badges: [...existing, badgeForLead] };
                }),
            );

            queryClient.setQueryData<LeadQueryData>(
              buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
              (old) => {
                if (!old || old.id !== variables.leadId) return old;
                const existing = old.badges ?? [];
                if (existing.some((b) => b.id === variables.badgeId))
                  return old;
                return { ...old, badges: [...existing, badgeForLead] };
              },
            );
          }
        }

        return { oldBadges, oldLeads, oldLead };
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
      },
      onError: async (_error, variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
        });

        queryClient.setQueryData(
          buildQueryKey(BADGES_QUERY_KEY),
          context?.oldBadges,
        );
        queryClient.setQueryData(
          buildQueryKey(LEADS_QUERY_KEY),
          context?.oldLeads,
        );
        queryClient.setQueryData(
          buildQueryKey([LEADS_QUERY_KEY, variables.leadId]),
          context?.oldLead,
        );
      },
    });

  return {
    attachBadgeAsync,
    attachBadgeIsPending,
  };
}
